import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { sportCategories } from '../seedData';
import { Search, MapPin, Star, TrendingUp, ChevronRight, Filter } from 'lucide-react';
import SkeletonCard from './SkeletonCard'; // <-- ADD THIS LINE
import ParallaxSection from './ParallaxSection';
import ParallaxBackground from './ParallaxBackground';
import { useTranslation } from 'react-i18next';

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime;
    let animationFrame;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      setCount(Math.floor(progress * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);

  return <span>{count}{suffix}</span>;
};

// Sport configuration with action background images - BLACK & GOLD THEME
const getSportConfig = (sportId) => {
  const configs = {
    football: {
      // Player kicking football
      bgImage: 'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=400&h=400&fit=crop&q=80',
      overlay: 'from-primary/80 to-amber-900/80',
      name: 'Football'
    },
    basketball: {
      // Player dunking
      bgImage: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=400&h=400&fit=crop&q=80',
      overlay: 'from-accent/80 to-yellow-900/80',
      name: 'Basketball'
    },
    padel: {
      // Padel player serving
      bgImage: 'https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=400&h=400&fit=crop&q=80',
      overlay: 'from-primary-600/80 to-amber-800/80',
      name: 'Padel'
    },
    volleyball: {
      // Volleyball player spiking
      bgImage: 'https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?w=400&h=400&fit=crop&q=80',
      overlay: 'from-amber-500/80 to-yellow-800/80',
      name: 'Volleyball'
    },
    tennis: {
      // Tennis player swinging
      bgImage: 'https://images.unsplash.com/photo-1622163642998-1ea32b0bbc67?w=400&h=400&fit=crop&q=80',
      overlay: 'from-primary-500/80 to-accent/80',
      name: 'Tennis'
    }
  };
  return configs[sportId] || configs.football;
};

// Sport Icon Badge with Action Background Images
const SportIconBadge = ({ sportId }) => {
  const config = getSportConfig(sportId);
  
  return (
    <div className="w-full flex justify-center mb-4">
      <div className="relative w-32 h-32 rounded-2xl overflow-hidden shadow-lg group-hover:shadow-2xl transition-all duration-300 group-hover:scale-105">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center transition-transform duration-300 group-hover:scale-110"
          style={{ backgroundImage: `url('${config.bgImage}')` }}
        />
        {/* Subtle Dark Overlay for readability - no gold tint */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-black/20 to-transparent transition-opacity duration-300 group-hover:opacity-60" />
        {/* Sport Name Overlay */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-white font-bold text-lg drop-shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            {config.name}
          </div>
        </div>
      </div>
    </div>
  );
};

const Homepage = ({ onSelectStadium, onSelectCategory }) => {
  const { t, i18n } = useTranslation();
  const [stadiums, setStadiums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const handleSearchSubmit = (e) => {
    e.preventDefault(); // This stops the page from reloading
    onSelectCategory('all', searchQuery);
  };

  useEffect(() => {
    loadStadiums();
  }, []);


  const loadStadiums = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'stadiums'));
      const stadiumsData = [];
      querySnapshot.forEach((doc) => {
        stadiumsData.push({ id: doc.id, ...doc.data() });
      });
      setStadiums(stadiumsData);
    } catch (error) {
      console.error('Error loading stadiums:', error);
    }
    setLoading(false);
  };

  const featuredStadiums = stadiums.filter(s => s.featured).slice(0, 3);
  const totalStadiums = stadiums.length;
  const totalSports = stadiums.reduce((sum, s) => sum + (s.sports?.length || 0), 0);

  return (
    <div className="bg-gradient-to-br from-amber-50 via-white to-yellow-50 dark:from-black dark:via-neutral-900 dark:to-black transition-colors">
      
      {/* Hero Section with Animated Gradient - BLACK & GOLD */}
      <div className="relative overflow-hidden bg-gradient-to-br from-neutral-900 via-neutral-800 to-black dark:from-black dark:via-neutral-900 dark:to-neutral-800">
        {/* Animated Gradient Overlay - GOLD */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-accent/20 to-amber-500/20 animate-gradient-x"></div>
        
        {/* Floating Orbs - GOLD */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-amber-400/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '4s' }}></div>
        
        <div className="relative max-w-7xl mx-auto px-6 py-16 md:py-24">
          <div className="text-center text-white">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 drop-shadow-2xl animate-fade-in-up">
              {t('homepage.heroTitle')}
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-white/90 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              {t('homepage.subtitle', 'The Premium Sports Venue Booking Platform')}
            </p>
            <p className="text-lg text-white/80 mb-12 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              {t('homepage.heroSports')}
            </p>

            {/* Search Bar with Glassmorphism */}
            <div className="max-w-3xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
              <form onSubmit={handleSearchSubmit}>
                <div className="bg-white/95 dark:bg-neutral-800/95 backdrop-blur-xl rounded-2xl shadow-2xl p-3 flex flex-col md:flex-row gap-3 border border-white/20 dark:border-neutral-700/50">
                  <div className="flex-1 flex items-center gap-3 px-4 py-3 md:py-0">
                    <Search size={24} className="text-neutral-400 dark:text-neutral-500" />
                    <input
                      type="text"
                      placeholder={t('homepage.searchPlaceholder')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="flex-1 outline-none text-lg text-neutral-800 dark:text-neutral-100 placeholder-neutral-400 dark:placeholder-neutral-500 bg-transparent w-full border-0 focus:ring-0 focus:outline-none"
                    />
                  </div>
                  <button 
                    type="submit"
                    className="luxury-button bg-gradient-to-r from-primary to-accent text-white px-8 py-4 rounded-xl font-bold hover:from-accent hover:to-primary transition-all duration-300 hover:shadow-gold-lg hover:scale-105"
                  >
                    {t('homepage.searchButton')}
                  </button>
                </div>
              </form>
            </div>

            {/* Quick Stats with Animated Counters */}
            <div className="flex justify-center gap-8 mt-12 animate-fade-in-up" style={{ animationDelay: '0.8s' }}>
              <div className="text-center transform hover:scale-110 transition-transform duration-300">
                <div className="text-4xl font-bold mb-1">
                  <AnimatedCounter end={totalStadiums} suffix="+" duration={1500} />
                </div>
                <div className="text-white/80">{t('homepage.statsVenues')}</div>
              </div>
              <div className="text-center transform hover:scale-110 transition-transform duration-300">
                <div className="text-4xl font-bold mb-1">
                  <AnimatedCounter end={totalSports} suffix="+" duration={1500} />
                </div>
                <div className="text-white/80">{t('homepage.statsFields')}</div>
              </div>
              <div className="text-center transform hover:scale-110 transition-transform duration-300">
                <div className="text-4xl font-bold mb-1">
                  <AnimatedCounter end={5} duration={1500} />
                </div>
                <div className="text-white/80">{t('homepage.statsSports')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Categories Section */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <ParallaxSection animation="fade-up" className="text-center mb-12">
          <h2 className="text-4xl font-bold text-neutral-800 dark:text-neutral-100 mb-4">{t('homepage.browseTitle')}</h2>
          <p className="text-xl text-neutral-600 dark:text-neutral-300">{t('homepage.browseSubtitle')}</p>
        </ParallaxSection>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {sportCategories.map((category, index) => (
            <ParallaxSection
              key={category.id}
              animation="fade-up"
              delay={100 * index}
            >
              <button
                onClick={() => onSelectCategory(category.id)}
                className="group glass-card glass-card-hover rounded-2xl p-6 relative overflow-hidden w-full h-full min-h-[320px] flex flex-col"
              >
                {/* Shimmer effect on hover */}
                <div className="absolute inset-0 shimmer opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="text-center relative z-10 flex flex-col flex-1">
                  {/* Modern gradient badge icon */}
                  <SportIconBadge sportId={category.id} />
                  
                  {/* Use the correct language for name and description */}
                  <h3 className="text-xl font-bold text-neutral-800 dark:text-neutral-100 mb-2 group-hover:text-primary dark:group-hover:text-primary-400 transition-colors">{i18n.language === 'ar' ? category.nameAr : category.name}</h3>
                  <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-3 min-h-[40px]">{i18n.language === 'ar' ? category.descriptionAr : category.description}</p>
                  <div className="flex items-center justify-center gap-2 text-primary dark:text-primary-400 font-semibold group-hover:gap-3 transition-all mt-auto">
                    <span>{t('homepage.explore')}</span>
                    <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </button>
            </ParallaxSection>
          ))}
        </div>
      </div>

      {/* Featured Stadiums */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <ParallaxSection animation="fade-up" className="flex items-center justify-between mb-12">
          <div>
            <h2 className="text-4xl font-bold text-neutral-800 dark:text-neutral-100 mb-2">{t('homepage.featuredTitle')}</h2>
            <p className="text-xl text-neutral-600 dark:text-neutral-300">{t('homepage.featuredSubtitle')}</p>
          </div>
          <button 
            onClick={() => onSelectCategory('all')}
            className="luxury-button flex items-center gap-2 text-primary dark:text-primary-400 font-semibold hover:text-primary-dark dark:hover:text-primary-300 transition-all hover:gap-3"
          >
            <span>{t('homepage.viewAll')}</span>
            <ChevronRight size={20} className="transition-transform" />
          </button>
        </ParallaxSection>

        {/* --- ENHANCED STADIUM CARDS --- */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {loading ? (
            // Show 3 skeletons while loading
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : (
            // Show the real stadium cards with glass effect
            featuredStadiums.map((stadium, index) => (
              <ParallaxSection
                key={stadium.id}
                animation="fade-up"
                delay={100 * index}
              >
                <button
                  onClick={() => onSelectStadium(stadium.id)}
                  className="group glass-card glass-card-hover rounded-2xl overflow-hidden text-left w-full"
                >
                {/* Image with overlay effects */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={stadium.photos[0]}
                    alt={stadium.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  
                  {/* Glass Rating Badge - GOLD */}
                  <div className="absolute bottom-4 left-4 bg-white/95 dark:bg-neutral-800/95 backdrop-blur-xl px-4 py-2 rounded-full flex items-center gap-2 shadow-gold border border-primary/20">
                    <Star size={14} className="text-primary fill-primary" />
                    <span className="font-bold text-neutral-800 dark:text-white">{stadium.rating}</span>
                    <span className="text-neutral-600 dark:text-neutral-400 text-sm">({stadium.reviewCount})</span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-neutral-800 dark:text-neutral-100 mb-2 group-hover:text-primary dark:group-hover:text-primary-400 transition-colors">
                    {stadium.name}
                  </h3>
                  <div className="flex items-center gap-2 text-neutral-600 dark:text-neutral-300 mb-4">
                    <MapPin size={16} />
                    <span>{stadium.location}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {stadium.sports.slice(0, 3).map((sport, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-gradient-to-r from-primary/10 to-accent/10 dark:from-primary/20 dark:to-accent/20 text-primary dark:text-primary-300 rounded-full text-sm font-semibold border border-primary/20"
                      >
                        {sport.type}
                      </span>
                    ))}
                    {stadium.sports.length > 3 && (
                      <span className="px-3 py-1 bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 rounded-full text-sm font-semibold">
                        +{stadium.sports.length - 3}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-neutral-200 dark:border-neutral-700">
                    <div>
                      <div className="text-sm text-neutral-600 dark:text-neutral-400">{t('homepage.startingFrom')}</div>
                      <div className="text-2xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                        ${Math.min(...stadium.sports.map(s => s.pricePerSlot))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-primary dark:text-primary-400 font-semibold">
                      <span>{t('homepage.bookNow')}</span>
                      <ChevronRight size={16} />
                    </div>
                  </div>
                </div>
              </button>
              </ParallaxSection>
            ))
          )}
        </div>
      </div>

      {/* How It Works */}
      <ParallaxBackground>
        <div className="bg-white/80 dark:bg-neutral-900/80 py-16 transition-colors backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-6">
            <ParallaxSection animation="fade-up" className="text-center mb-12">
              <h2 className="text-4xl font-bold text-neutral-800 dark:text-neutral-100 mb-4">{t('homepage.howItWorksTitle')}</h2>
              <p className="text-xl text-neutral-600 dark:text-neutral-300">{t('homepage.howItWorksSubtitle')}</p>
            </ParallaxSection>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <ParallaxSection animation="fade-up" delay={100}>
                <div className="text-center group hover:scale-105 transition-transform duration-300">
                  <div className="w-20 h-20 bg-gradient-to-br from-primary-400 to-primary-600 dark:from-primary-500 dark:to-primary-700 rounded-full flex items-center justify-center mx-auto mb-6 shadow-gold group-hover:shadow-luxury transition-shadow duration-300">
                    <Search size={40} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-neutral-800 dark:text-neutral-100 mb-3">{t('homepage.step1Title')}</h3>
                  <p className="text-neutral-600 dark:text-neutral-300">{t('homepage.step1Subtitle')}</p>
                </div>
              </ParallaxSection>
              <ParallaxSection animation="fade-up" delay={200}>
                <div className="text-center group hover:scale-105 transition-transform duration-300">
                  <div className="w-20 h-20 bg-gradient-to-br from-accent-400 to-accent-600 dark:from-accent-500 dark:to-accent-700 rounded-full flex items-center justify-center mx-auto mb-6 shadow-gold group-hover:shadow-luxury transition-shadow duration-300">
                    <Filter size={40} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-neutral-800 dark:text-neutral-100 mb-3">{t('homepage.step2Title')}</h3>
                  <p className="text-neutral-600 dark:text-neutral-300">{t('homepage.step2Subtitle')}</p>
                </div>
              </ParallaxSection>
              <ParallaxSection animation="fade-up" delay={300}>
                <div className="text-center group hover:scale-105 transition-transform duration-300">
                  <div className="w-20 h-20 bg-gradient-to-br from-primary-400 to-accent-500 dark:from-primary-500 dark:to-accent-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-gold group-hover:shadow-luxury transition-shadow duration-300">
                    <TrendingUp size={40} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-neutral-800 dark:text-neutral-100 mb-3">{t('homepage.step3Title')}</h3>
                  <p className="text-neutral-600 dark:text-neutral-300">{t('homepage.step3Subtitle')}</p>
                </div>
              </ParallaxSection>
            </div>
          </div>
        </div>
      </ParallaxBackground>

      {/* CTA Section */}
      <div className="bg-gradient-to-br from-primary-600 via-primary-500 to-accent-500 dark:from-primary-800 dark:via-primary-700 dark:to-accent-700 py-20 transition-colors relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-accent-300/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
        </div>
        
        <ParallaxSection animation="scale" className="max-w-4xl mx-auto text-center px-6 relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('homepage.ctaTitle')}
          </h2>
          <p className="text-xl text-white/90 mb-8">
            {t('homepage.ctaSubtitle')}
          </p>
          <button
            onClick={() => onSelectCategory('all')}
            className="group relative bg-white text-primary-600 px-8 py-4 rounded-xl font-bold text-lg hover:scale-105 transition-all duration-300 shadow-luxury hover:shadow-2xl overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-accent-400 to-primary-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <span className="relative z-10 group-hover:text-white transition-colors duration-300">{t('homepage.ctaButton')}</span>
          </button>
        </ParallaxSection>
      </div>
    </div>
  );
};

export default Homepage;